// ABOUTME: Displays a Pure query with its execution result table and export buttons.
// ABOUTME: Primary content block for the Goldman POC query evaluation flow.

import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Copy, Check, ChevronDown, ChevronUp, Download, FileText, Clock, Rows3 } from 'lucide-react';
import hljs from 'highlight.js/lib/core';
import sql from 'highlight.js/lib/languages/sql';
import 'highlight.js/styles/atom-one-dark.css';
import type { PureQueryResult } from '@/utils/exportUtils';
import { exportResultAsCsv, exportQueries } from '@/utils/exportUtils';
import { formatPureQuery } from '@/utils/pureFormatter';

// Register SQL for approximate Pure syntax highlighting
hljs.registerLanguage('sql', sql);

interface PureQueryResultBlockProps {
  result: PureQueryResult;
  /** Index of this query block within the message (for display numbering) */
  queryIndex?: number;
}

const MAX_VISIBLE_ROWS = 25;

export function PureQueryResultBlock({ result, queryIndex }: PureQueryResultBlockProps) {
  const [copied, setCopied] = useState(false);
  const [showSql, setShowSql] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);

  const visibleRows = showAllRows ? result.rows : result.rows.slice(0, MAX_VISIBLE_ROWS);
  const hasMoreRows = result.rows.length > MAX_VISIBLE_ROWS;

  const formattedQuery = useMemo(() => formatPureQuery(result.pure_query), [result.pure_query]);

  const highlightedQuery = useMemo(() => {
    try {
      return hljs.highlight(formattedQuery, { language: 'sql', ignoreIllegals: true }).value;
    } catch {
      return formattedQuery
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }, [formattedQuery]);

  const handleCopyQuery = async () => {
    await navigator.clipboard.writeText(result.pure_query);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportCsv = () => {
    const suffix = queryIndex !== undefined ? `-${queryIndex + 1}` : '';
    exportResultAsCsv(result, `query-result${suffix}.csv`);
  };

  const handleExportQuery = () => {
    const suffix = queryIndex !== undefined ? `-${queryIndex + 1}` : '';
    exportQueries([result.pure_query], `pure-query${suffix}.pure`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="rounded-sm border border-surface-200 bg-surface-0 shadow-sm overflow-hidden"
    >
      {/* Query Section — follows CodeBlock visual pattern */}
      <div className="flex items-center justify-between px-4 py-2 bg-surface-50 border-b border-surface-200">
        <span className="text-xs font-mono text-surface-500 uppercase tracking-wider">Pure</span>
        <button
          onClick={handleCopyQuery}
          className="p-1 rounded-sm hover:bg-surface-200 text-surface-500 hover:text-surface-800 hover:scale-110 transition-all cursor-pointer"
          aria-label="Copy query"
        >
          {copied ? (
            <Check className="w-4 h-4 text-success" />
          ) : (
            <Copy className="w-4 h-4" />
          )}
        </button>
      </div>
      <pre className="overflow-x-auto">
        <code
          className="language-sql text-sm font-mono hljs !px-4 !py-3.5 !leading-relaxed"
          dangerouslySetInnerHTML={{ __html: highlightedQuery }}
        />
      </pre>

      {/* Generated SQL (collapsible) */}
      {result.generated_sql && (
        <div className="border-t border-surface-200">
          <button
            onClick={() => setShowSql(!showSql)}
            className="w-full flex items-center gap-1.5 px-4 py-2 text-xs text-surface-400 hover:text-surface-600 hover:bg-surface-50 transition-colors cursor-pointer"
          >
            {showSql ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            <span>Generated SQL</span>
          </button>
          <AnimatePresence>
            {showSql && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="overflow-hidden"
              >
                <pre className="px-4 py-3 overflow-x-auto bg-surface-50 border-t border-surface-100">
                  <code className="text-xs font-mono text-surface-500 whitespace-pre-wrap break-all">
                    {result.generated_sql}
                  </code>
                </pre>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Result Table */}
      <div className="border-t border-surface-200">
        {/* Result header with metadata */}
        <div className="flex items-center gap-3 px-4 py-2.5 bg-surface-50 border-b border-surface-200">
          <span className="label">Results</span>
          <div className="flex items-center gap-2 text-surface-400">
            <span className="flex items-center gap-1 text-xs">
              <Rows3 className="w-3 h-3" />
              {result.row_count} {result.row_count === 1 ? 'row' : 'rows'}
            </span>
            {result.execution_time_ms > 0 && (
              <span className="flex items-center gap-1 text-xs">
                <Clock className="w-3 h-3" />
                {result.execution_time_ms < 1000
                  ? `${Math.round(result.execution_time_ms)}ms`
                  : `${(result.execution_time_ms / 1000).toFixed(1)}s`}
              </span>
            )}
          </div>
        </div>

        {/* Table — data-table design system pattern */}
        {result.columns.length > 0 && result.rows.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  {result.columns.map((col, i) => (
                    <th key={i} className="whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, rowIdx) => (
                  <tr key={rowIdx}>
                    {row.map((val, colIdx) => (
                      <td
                        key={colIdx}
                        className={`whitespace-nowrap ${
                          typeof val === 'number' ? 'data-value text-right' : ''
                        }`}
                      >
                        {formatCell(val)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Show more/less toggle */}
            {hasMoreRows && (
              <button
                onClick={() => setShowAllRows(!showAllRows)}
                className="w-full py-2 text-xs text-primary-600 hover:text-primary-700 hover:bg-primary-50 transition-colors cursor-pointer border-t border-surface-100"
              >
                {showAllRows
                  ? `Show fewer rows`
                  : `Show all ${result.row_count} rows (${result.row_count - MAX_VISIBLE_ROWS} more)`}
              </button>
            )}
          </div>
        ) : (
          <div className="px-4 py-6 text-center text-sm text-surface-400">
            No rows returned
          </div>
        )}
      </div>

      {/* Export Actions */}
      <div className="flex items-center gap-2 px-4 py-3 bg-surface-50 border-t border-surface-200">
        <button
          onClick={handleExportCsv}
          className="btn btn-sm btn-secondary gap-1.5"
          disabled={result.rows.length === 0}
        >
          <Download className="w-3.5 h-3.5" />
          Export CSV
        </button>
        <button
          onClick={handleExportQuery}
          className="btn btn-sm btn-ghost gap-1.5"
        >
          <FileText className="w-3.5 h-3.5" />
          Export Query
        </button>
      </div>
    </motion.div>
  );
}

/**
 * Renders a footer button to export all Pure queries from a message.
 */
export function ExportAllQueriesButton({ queries }: { queries: string[] }) {
  if (queries.length === 0) return null;

  const handleExport = () => {
    exportQueries(queries, `all-queries-${Date.now()}.pure`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
      className="flex justify-start"
    >
      <button
        onClick={handleExport}
        className="btn btn-sm btn-secondary gap-1.5"
      >
        <Download className="w-3.5 h-3.5" />
        Export All Queries ({queries.length})
      </button>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Cell formatting
// ---------------------------------------------------------------------------

function formatCell(val: unknown): string {
  if (val === null || val === undefined) return '\u2014'; // em dash
  if (typeof val === 'number') {
    if (Number.isInteger(val)) return val.toLocaleString();
    return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return String(val);
}
